/**
 * Single Command
 */
import {Appendat, TextAppendat} from "./appendat";
import {CommandClause} from "../print/command_clause";
import {Checker} from "../../../toolkit/";

export abstract class SingleCommand<T> {
  /**
   * arguments
   * @private
   */
  private readonly _arguments: Array<T>;
  private readonly _symbolAfterHeader: string;
  private readonly _symbolBetweenArguments: string;

  private _header?: T;
  private _charset: string;

  protected constructor(symbolAfterHeader: string, symbolBetweenArguments: string, charset: string) {
    this._arguments = [];
    this._symbolAfterHeader = symbolAfterHeader;
    this._symbolBetweenArguments = symbolBetweenArguments;
    this._charset = charset;
  }

  protected get getHeader(): T {
    return this._header
  }

  protected get symbolAfterHeader(): string {
    return this._symbolAfterHeader;
  }

  protected get symbolBetweenArguments(): string {
    return this._symbolBetweenArguments;
  }

  public header(header: T): SingleCommand<T> {
    this._header = header;
    return this
  }

  protected get charset(): string {
    return this._charset;
  }

  public append(appendat: Appendat<T>): SingleCommand<T> {
    if (!appendat.condition) return this;
    const argument = appendat.argument();
    if (!Checker.truthy(argument))
      return this;
    this._arguments.push(argument);
    return this;
  }

  public arguments(): Array<T> {
    return this._arguments;
  }

  public abstract output(): T;
}

abstract class BasicStringSingleCommand extends SingleCommand<string> {
  protected constructor(symbolAfterHeader: string, symbolBetweenArguments: string, charset: string) {
    super(symbolAfterHeader, symbolBetweenArguments, charset);
  }

  append(appendat: Appendat<string>): BasicStringSingleCommand {
    const argument = appendat.argument();
    if (!Checker.truthy(argument)) return this;
    super.append(appendat);
    return this;
  }

  public appendText(text?: string): BasicStringSingleCommand {
    return this.append(TextAppendat.create(text ?? '', {}))
  }

  public appendNumber(num?: number): BasicStringSingleCommand {
    if (isNaN(num)) return this;
    return this.append(TextAppendat.create((num ?? '').toString(), {}))
  }

  output(): string {
    let ret = '';
    if (Checker.truthy(super.getHeader)) {
      ret += super.getHeader;
      ret += super.symbolAfterHeader;
    }
    const args = super.arguments();
    const len = args.length;
    let seq = 0;
    for (const arg of args) {
      seq += 1;
      if (!Checker.truthy(arg)) continue;
      ret += arg;
      if (seq < len) {
        ret += super.symbolBetweenArguments;
      }
    }
    return ret;
  }

  public clause(): CommandClause {
    const output = this.output();
    if (!Checker.truthy(output)) {
      return CommandClause.none();
    }
    return CommandClause.text(output, super.charset)
  }
}

export class CPCLCommand extends BasicStringSingleCommand {

  public constructor(header: string, charset?: string) {
    super(' ', ' ', charset);
    super.header(header);
  }

  public static with(header: string, charset?: string): CPCLCommand {
    return new CPCLCommand(header, charset);
  }

  append(appendat: Appendat<string>): CPCLCommand {
    super.append(appendat);
    return this;
  }

  appendText(text?: string): CPCLCommand {
    super.appendText(text);
    return this;
  }

  appendNumber(num?: number): CPCLCommand {
    super.appendNumber(num);
    return this;
  }

}

export class TSPLCommand extends BasicStringSingleCommand {

  public constructor(header: string, charset?: string) {
    super(' ', ',', charset);
    super.header(header);
  }

  public static with(header: string, charset?: string): TSPLCommand {
    return new TSPLCommand(header, charset);
  }

  append(appendat: Appendat<string>): TSPLCommand {
    super.append(appendat);
    return this;
  }

  appendText(text?: string): TSPLCommand {
    super.appendText(text);
    return this;
  }

  appendNumber(num?: number): TSPLCommand {
    super.appendNumber(num);
    return this;
  }
}
